import React, { useEffect, useRef, useState } from 'react';

export type CheckboxMultiSelectOption = {
  value: string;
  label: string;
  detail?: string;
};

type Props = {
  label: string;
  /** Empty list means "None" (stored as null by the caller). */
  value: readonly string[];
  options: readonly CheckboxMultiSelectOption[];
  onChange: (next: string[]) => void;
  noneLabel?: string;
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
};

/**
 * Dropdown of checkboxes. Selecting "None" clears the list; picking any option
 * deselects None.
 */
const CheckboxMultiSelect: React.FC<Props> = ({
  label,
  value,
  options,
  onChange,
  noneLabel = 'None',
  className,
  buttonClassName,
  labelClassName,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isNone = value.length === 0;
  const selectedLabels = options
    .filter((option) => value.includes(option.value))
    .map((option) => option.label);
  const summary = isNone ? noneLabel : selectedLabels.join(', ') || `${value.length} selected`;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((id) => id !== optionValue));
      return;
    }
    onChange([...value, optionValue]);
  };

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <label className={labelClassName ?? 'mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'}>
        {label}
      </label>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={
          buttonClassName ??
          'input-field flex w-full items-center justify-between gap-2 text-left text-sm'
        }
      >
        <span className={`truncate ${isNone ? 'text-slate-400' : ''}`} title={summary}>
          {summary}
        </span>
        <span className="shrink-0 text-[9px] text-slate-400">▼</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable
          aria-label={label}
          className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          <label className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-2.5 py-1.5 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={isNone}
              onChange={() => onChange([])}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-500">{noneLabel}</span>
          </label>
          {options.length === 0 ? (
            <p className="px-2.5 py-2 text-xs text-slate-400">No options available</p>
          ) : (
            options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => toggle(option.value)}
                  className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="min-w-0 flex-1 flex items-center justify-between gap-2">
                  <span className="truncate text-sm text-slate-800">{option.label}</span>
                  {option.detail ? (
                    <span className="shrink-0 text-[11px] font-medium text-slate-500">
                      {option.detail}
                    </span>
                  ) : null}
                </span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CheckboxMultiSelect;
